/**
 * Filter ranked Soren cards by server feed-state (done / snoozed / suppress).
 */

export interface SorenFeedFilterState {
  suppressions?: string[]
  snoozed?: string[]
  done?: string[]
}

/**
 * Filter ranked Soren cards by server feed-state (done / snoozed / suppress).
 *
 * Rich rule key fingerprinting:
 * - Money:     `kind:projectId:sum_cents:count`
 * - Deadlines: `kind:projectId:deadline_date`
 * - Documents: `kind:projectId:count`
 *
 * Backward-compatible: matches legacy `kind:projectId`.
 * Re-fire on suppress if money amount grows by >10% or count increases.
 */

export interface SorenFeedFilterState {
  suppressions?: string[]
  snoozed?: string[]
  done?: string[]
}

export interface SorenFactInput {
  amount?: number
  sum_cents?: number
  payAppCount?: number
  count?: number
  docCount?: number
  deadlineDate?: string | null
  deadline_date?: string | null
}

export function makeSorenRuleKey(kind: string, projectId: number, facts: SorenFactInput = {}): string {
  const k = String(kind || '').trim()
  const pid = Math.trunc(projectId)

  if (k === 'money') {
    const sumCents =
      facts.sum_cents != null
        ? Math.round(Number(facts.sum_cents))
        : facts.amount != null
          ? Math.round(Number(facts.amount) * 100)
          : null
    const count =
      facts.count != null
        ? Math.trunc(facts.count)
        : facts.payAppCount != null
          ? Math.trunc(facts.payAppCount)
          : null
    if (sumCents != null && count != null) {
      return `${k}:${pid}:${sumCents}:${count}`
    }
  } else if (k === 'deadline') {
    const dateStr = facts.deadline_date || facts.deadlineDate
    if (dateStr) {
      return `${k}:${pid}:${String(dateStr).slice(0, 10)}`
    }
  } else if (k === 'document') {
    const count =
      facts.count != null
        ? Math.trunc(facts.count)
        : facts.docCount != null
          ? Math.trunc(facts.docCount)
          : null
    if (count != null) {
      return `${k}:${pid}:${count}`
    }
  }

  return `${k}:${pid}`
}

export function parseSorenRuleKey(key: string) {
  const s = String(key || '').trim()
  const moneyMatch = s.match(/^([a-z_]+):(\d+):(\d+):(\d+)$/i)
  if (moneyMatch && moneyMatch[1] === 'money') {
    return {
      kind: moneyMatch[1],
      projectId: parseInt(moneyMatch[2], 10),
      sum_cents: parseInt(moneyMatch[3], 10),
      count: parseInt(moneyMatch[4], 10),
    }
  }

  const deadlineMatch = s.match(/^([a-z_]+):(\d+):(\d{4}-\d{2}-\d{2})$/i)
  if (deadlineMatch && deadlineMatch[1] === 'deadline') {
    return {
      kind: deadlineMatch[1],
      projectId: parseInt(deadlineMatch[2], 10),
      deadline_date: deadlineMatch[3],
    }
  }

  const docMatch = s.match(/^([a-z_]+):(\d+):(\d+)$/i)
  if (docMatch && docMatch[1] === 'document') {
    return {
      kind: docMatch[1],
      projectId: parseInt(docMatch[2], 10),
      count: parseInt(docMatch[3], 10),
    }
  }

  const legacyMatch = s.match(/^([a-z_]+):(\d+)$/i)
  if (!legacyMatch) return null
  return { kind: legacyMatch[1], projectId: parseInt(legacyMatch[2], 10) }
}

function cardFacts(card: Record<string, unknown>): SorenFactInput {
  return {
    amount: typeof card.amount === 'number' ? card.amount : undefined,
    payAppCount: typeof card.payAppCount === 'number' ? card.payAppCount : undefined,
    count: typeof card.count === 'number' ? card.count : undefined,
    docCount: typeof card.docCount === 'number' ? card.docCount : undefined,
    deadlineDate: typeof card.deadlineDate === 'string' ? card.deadlineDate : undefined,
    deadline_date: typeof card.deadline_date === 'string' ? card.deadline_date : undefined,
  }
}

function checkIsCardSuppressed(
  card: { kind: string; projectId: number },
  suppressionsSet: Set<string>,
  suppressionList: string[],
): boolean {
  const facts = cardFacts(card as unknown as Record<string, unknown>)
  const exactKey = makeSorenRuleKey(card.kind, card.projectId, facts)
  if (suppressionsSet.has(exactKey)) return true

  // Legacy key check
  const legacyKey = `${card.kind}:${card.projectId}`
  if (suppressionsSet.has(legacyKey)) {
    if (card.kind === 'money') return false // Re-fire legacy money cards
    return true
  }

  // If card is money, check if amount grew by >10% or count increased
  if (card.kind === 'money') {
    for (const supKey of suppressionList) {
      const parsed = parseSorenRuleKey(supKey)
      if (parsed && parsed.kind === 'money' && parsed.projectId === card.projectId) {
        const currentCents = facts.sum_cents ?? (facts.amount != null ? Math.round(facts.amount * 100) : 0)
        const currentCount = facts.count ?? facts.payAppCount ?? 1
        const suppressedCents = parsed.sum_cents ?? 0
        const suppressedCount = parsed.count ?? 1

        // If new pay app joined, re-fire!
        if (currentCount > suppressedCount) return false

        // If amount grew by > 10%, re-fire!
        if (suppressedCents > 0 && currentCents > suppressedCents * 1.10) return false

        return true
      }
    }
  }

  return false
}

function checkCardKeyMatched(card: { kind: string; projectId: number }, keySet: Set<string>): boolean {
  const facts = cardFacts(card as unknown as Record<string, unknown>)
  const exactKey = makeSorenRuleKey(card.kind, card.projectId, facts)
  if (keySet.has(exactKey)) return true
  const legacyKey = `${card.kind}:${card.projectId}`
  if (keySet.has(legacyKey)) return true
  return false
}

export function filterSorenCardsByFeedState<
  T extends { kind: string; projectId: number },
>(cards: T[], state: SorenFeedFilterState | null | undefined): T[] {
  const suppressionList = state?.suppressions || []
  const suppressions = new Set(suppressionList)
  const snoozed = new Set(state?.snoozed || [])
  const done = new Set(state?.done || [])

  return (cards || []).filter((c) => {
    if (c.projectId == null || !Number.isFinite(c.projectId)) return true
    if (checkIsCardSuppressed(c, suppressions, suppressionList)) return false
    if (checkCardKeyMatched(c, snoozed)) return false
    if (checkCardKeyMatched(c, done)) return false
    return true
  })
}
